import { CatGraph, CatGraphEvidence, CatGraphNode } from '../../../generated.commonjs/cat_graph_pb';
import { RawEvidenceCitation, mapEvidenceChain } from '../citations/evidence-citation.mapper';
import {
  CitationSet,
  EvidenceCitation,
  NormalizedBox,
  PageAnchoredCitation,
} from '../citations/evidence-citation.model';
import { MAX_HIGHLIGHT_REGIONS } from '../highlight-regions';

/**
 * What a CAT graph's evidence chip opens (Issue #2399).
 *
 * A chip names citations by ID (`CatGraphEvidence.citation_ids`, and `data-citations` in the
 * picture). The citations themselves are in the graph's table, in the product's own evidence type
 * from #1773, so they go through the same mapper as a report's evidence: a citation that names
 * another project, a malformed page or a malformed box is refused there, and never becomes a link.
 *
 * This module is pure (no Angular), so it can be tested without a component.
 *
 * How a plan region is located. A citation that names a region path opens its region by path, as
 * everywhere else in the app, even when it also stores a box: the path is the region's lasting
 * identity, while a box is a snapshot that can go stale when the plan is ingested again. When a
 * chip's first plan citation is such a region, the chip opens that region; its other citations are
 * left out, as a link to one region can name only one.
 *
 * The golden graphs name no region path: they locate each plan region by a box on the page, and the
 * mapper keeps a box only on a citation of kind "region". For those, this module reads the box from
 * the citation as stored, and the Preview highlights it. A box is tied to the revision of the page it
 * was measured on, so the view says that a highlight may be out of place after a new ingestion.
 */

/** A graph's citations, validated, with the box each one stated. */
export interface GraphCitations {
  set: CitationSet;
  /** The box each citation stated, by citation ID, in fractions of the page. */
  boxes: ReadonlyMap<string, NormalizedBox>;
}

/** Validates a graph's citation table against the graph's own project. */
export function graphCitations(graph: CatGraph): GraphCitations {
  const raw = graph.getCitationsList().map(citation => citation.toObject() as RawEvidenceCitation);
  const set = mapEvidenceChain(raw, { planId: graph.getArchitecturalPlanId(), fileId: '', pageNumber: 0 });
  const boxes = new Map<string, NormalizedBox>();
  for (const entry of raw) {
    const box = fractionBox(entry.normalizedBox);
    if (box && entry.citationId && set.byId.has(entry.citationId)) boxes.set(entry.citationId, box);
  }
  return { set, boxes };
}

/** What one chip opens. */
export type ChipDestination =
  /** One page of the plan, with the regions of that page the chip cites. */
  | { kind: 'page'; citation: PageAnchoredCitation; boxes: NormalizedBox[]; otherPages: number }
  /** A citation that opens by itself: a region by path, a passage of text, or a code provision. */
  | { kind: 'citation'; citation: EvidenceCitation }
  /** Nothing can be opened. */
  | { kind: 'none'; reason: string };

/**
 * Works out what a chip opens from the IDs it names.
 *
 * The citations of one chip normally lie on one sheet, and they open as one link with every box
 * highlighted (up to the Preview's limit). When a chip's citations lie on several pages, the page of
 * the first one opens and `otherPages` says how many pages were left out, so the view can say so.
 */
export function chipDestination(citationIds: readonly string[], citations: GraphCitations): ChipDestination {
  const found = citationIds
    .map(id => citations.set.byId.get(id))
    .filter((citation): citation is EvidenceCitation => citation !== undefined);
  if (found.length === 0) {
    return { kind: 'none', reason: 'The evidence this chip names is not in the graph, or was refused.' };
  }

  const onPages = found.filter((citation): citation is PageAnchoredCitation => citation.kind !== 'code');
  if (onPages.length === 0) return { kind: 'citation', citation: found[0] };

  const first = onPages[0];
  if (first.kind === 'region') return { kind: 'citation', citation: first };

  const samePage = onPages.filter(citation => pageKey(citation) === pageKey(first));
  const boxes = samePage
    .map(citation => citations.boxes.get(citation.id))
    .filter((box): box is NormalizedBox => box !== undefined)
    .slice(0, MAX_HIGHLIGHT_REGIONS);
  const otherPages = new Set(onPages.map(pageKey)).size - 1;

  if (boxes.length === 0 && first.kind !== 'page') return { kind: 'citation', citation: first };
  return { kind: 'page', citation: first, boxes, otherPages };
}

/** One piece of a box's evidence, as the list of claims shows it. */
export interface ListedEvidence {
  label: string;
  description: string;
  /** The citations of this piece; empty for a document outside the app or a search scope. */
  citationIds: string[];
  /** For a document outside the app: what it is and where in it, as plain text. */
  external: string;
}

/** One claim of the graph, as the list of claims shows it. */
export interface ListedClaim {
  argumentId: string;
  nodeId: string;
  kind: string;
  role: string;
  summary: string;
  statement: string;
  source: string;
  evidence: ListedEvidence[];
}

/** One argument of the graph, as the list of claims shows it. */
export interface ListedArgument {
  argumentId: string;
  heading: string;
  caption: string;
  claims: ListedClaim[];
}

/** Words for each kind of box, as the picture writes them. */
const KIND_LABEL: Readonly<Record<number, string>> = Object.freeze({
  [CatGraphNode.Kind.PLAN]: 'Plan observation',
  [CatGraphNode.Kind.DOCUMENT]: 'Document observation',
  [CatGraphNode.Kind.CODE]: 'Code requirement',
  [CatGraphNode.Kind.ABSENCE]: 'Absence claim',
  [CatGraphNode.Kind.ASSUMPTION]: 'Assumption',
  [CatGraphNode.Kind.OBJECTIVE]: 'Objective',
  [CatGraphNode.Kind.INTERMEDIATE]: 'Intermediate conclusion',
  [CatGraphNode.Kind.CONCLUSION]: 'Conclusion',
  [CatGraphNode.Kind.CLAIM]: 'Their premise',
  [CatGraphNode.Kind.UNSTATED]: 'Their unstated premise',
});

/**
 * The graph's claims and their evidence, from the message alone. The view shows this when the
 * picture cannot be fetched, and on request. It is a list, not a drawing: the conclusion of each
 * argument comes first, as a reader of a finding wants it, and its premises follow in the order the
 * graph lists them.
 */
export function listClaims(graph: CatGraph): ListedArgument[] {
  return graph.getArgumentsList().map(argument => {
    const nodes = argument.getNodesList();
    const conclusions = nodes.filter(node => node.getKind() === CatGraphNode.Kind.CONCLUSION);
    const ordered = [...conclusions, ...nodes.filter(node => !conclusions.includes(node))];
    return {
      argumentId: argument.getArgumentId(),
      heading: argument.getHeading(),
      caption: argument.getCaption(),
      claims: ordered.map(node => ({
        argumentId: argument.getArgumentId(),
        nodeId: node.getNodeId(),
        kind: KIND_LABEL[node.getKind()] ?? 'Claim',
        role: node.getRole(),
        summary: node.getSummary() || node.getStatement(),
        statement: node.getStatement(),
        source: node.getSource(),
        evidence: node.getEvidenceList().map(listedEvidence),
      })),
    };
  });
}

/** One piece of a box's evidence, as the list of claims shows it. */
function listedEvidence(evidence: CatGraphEvidence): ListedEvidence {
  const external = evidence.getExternal();
  return {
    label: evidence.getLabel(),
    description: evidence.getDescription(),
    citationIds: evidence.getCitationIdsList(),
    external: external ? [external.getTitle(), external.getLocator()].filter(Boolean).join(', ') : '',
  };
}

/** The page a citation names, as one key. */
function pageKey(citation: PageAnchoredCitation): string {
  return `${citation.page.fileId || '1'}/${citation.page.pageNumber}`;
}

/** A box in fractions of the page, or `null` when it is absent, empty or out of range. */
function fractionBox(raw: RawEvidenceCitation['normalizedBox']): NormalizedBox | null {
  if (!raw) return null;
  const xMin = raw.xMin ?? 0;
  const yMin = raw.yMin ?? 0;
  const xMax = raw.xMax ?? 0;
  const yMax = raw.yMax ?? 0;
  const inRange = [xMin, yMin, xMax, yMax].every(value => Number.isFinite(value) && value >= 0 && value <= 1);
  if (!inRange || xMax <= xMin || yMax <= yMin) return null;
  return { x: xMin, y: yMin, width: xMax - xMin, height: yMax - yMin };
}
